import React, { useState, useEffect, useRef } from 'react';
import { staticCountries } from '../../utility/countries';

// Self-contained SVGs to avoid import dependencies and version mismatch
const PublicIconSVG = ({ style }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const SearchIconSVG = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#94a3b8" }}
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const ArrowIconSVG = ({ isOpen }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.2s",
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      color: "#94a3b8"
    }}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

function CountrySelector({ value, onChange, onFocus, onBlur, focused }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (onBlur) onBlur();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onBlur]);

  // Find currently selected country
  const selectedCountry = staticCountries.find(c => String(c.id) === String(value));

  // Filter countries based on search term
  const filteredCountries = staticCountries.filter(c => 
    c.nicename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phonecode.includes(searchTerm)
  );

  const handleSelect = (countryId) => {
    onChange(countryId);
    setIsOpen(false);
    setSearchTerm('');
    if (onBlur) onBlur();
  };

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      if (onBlur) onBlur();
    } else {
      setIsOpen(true);
      if (onFocus) onFocus();
    }
  };

  const getFlagUrl = (iso) => {
    if (!iso) return '';
    return `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger Button */}
      <div
        onClick={handleToggle}
        style={{
          width: "100%",
          borderRadius: 12,
          padding: "12px 14px 12px 40px",
          border: `1.5px solid ${focused ? "#2563eb" : "rgba(59,130,246,0.18)"}`,
          background: focused ? "#f0f7ff" : "#f8fafc",
          color: "#0f172a",
          fontSize: 13,
          fontFamily: "'DM Sans',sans-serif",
          outline: "none",
          boxSizing: "border-box",
          transition: "all 0.2s",
          boxShadow: focused ? "0 0 0 3px rgba(59,130,246,0.1)" : "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 45
        }}
      >
        {/* Left Icon (Flag or Globe Icon) */}
        <div style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
          display: "flex", alignItems: "center", pointerEvents: "none"
        }}>
          {selectedCountry ? (
            <img
              src={getFlagUrl(selectedCountry.iso)}
              alt={selectedCountry.iso}
              style={{
                width: 20,
                height: 14,
                objectFit: 'cover',
                borderRadius: 2.5,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                border: '0.5px solid rgba(0,0,0,0.08)'
              }}
            />
          ) : (
            <PublicIconSVG style={{ color: focused ? "#2563eb" : "#94a3b8", transition: "color 0.2s" }} />
          )}
        </div>

        {/* Selected Country text */}
        <span style={{
          color: selectedCountry ? "#0f172a" : "#94a3b8",
          fontSize: 13,
          fontFamily: "'DM Sans',sans-serif",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          textAlign: "left"
        }}>
          {selectedCountry ? selectedCountry.nicename : "Select your country"}
        </span>

        {/* Right Arrow Icon */}
        <ArrowIconSVG isOpen={isOpen} />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          marginTop: 6,
          background: "#ffffff",
          border: "1px solid rgba(59,130,246,0.12)",
          borderRadius: 14,
          boxShadow: "0 10px 25px -5px rgba(59,130,246,0.08), 0 8px 10px -6px rgba(59,130,246,0.08)",
          zIndex: 1000,
          overflow: "hidden",
          animation: "slideDown 0.15s ease-out"
        }}>
          {/* Search Bar */}
          <div style={{
            position: "relative",
            padding: "8px 10px",
            borderBottom: "1px solid rgba(59,130,246,0.06)",
            background: "#f8fafc"
          }}>
            <div style={{
              position: "absolute",
              left: 18,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center"
            }}>
              <SearchIconSVG />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search country..."
              autoFocus
              style={{
                width: "100%",
                padding: "8px 8px 8px 30px",
                borderRadius: 8,
                border: "1px solid rgba(59,130,246,0.12)",
                background: "#ffffff",
                color: "#0f172a",
                fontSize: 12,
                fontFamily: "'DM Sans',sans-serif",
                outline: "none",
                boxSizing: "border-box"
              }}
            />
          </div>

          {/* List Items */}
          <div style={{
            maxHeight: 220,
            overflowY: "auto",
            padding: "4px 0"
          }}>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((c) => {
                const isSelected = String(c.id) === String(value);
                return (
                  <div
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 14px",
                      cursor: "pointer",
                      background: isSelected ? "rgba(37,99,235,0.06)" : "transparent",
                      transition: "background 0.15s",
                      fontSize: 13,
                      color: isSelected ? "#2563eb" : "#334155",
                      fontWeight: isSelected ? 600 : 400,
                      fontFamily: "'DM Sans',sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "#f1f5f9";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <img
                      src={getFlagUrl(c.iso)}
                      alt={c.iso}
                      loading="lazy"
                      style={{
                        width: 20,
                        height: 14,
                        objectFit: 'cover',
                        borderRadius: 2.5,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                        border: '0.5px solid rgba(0,0,0,0.05)'
                      }}
                    />
                    <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.nicename}
                    </span>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>
                      {c.phonecode}
                    </span>
                  </div>
                );
              })
            ) : (
              <div style={{
                padding: "16px",
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 12,
                fontFamily: "'DM Sans',sans-serif"
              }}>
                No countries found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Animation Style */}
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default CountrySelector;
